import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import apiClient from "../utils/apiClient";
import { SERVER_BASE } from "../config/server";
import "bootstrap/dist/css/bootstrap.min.css";
import Swal from "sweetalert2";
import { permissionColumnGroups } from "./masterList";

const allPermissionKeys = Object.values(permissionColumnGroups).flatMap((g) =>
  Object.keys(g.items)
);

const PermissionsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const rowData = location.state?.rowData;
  const sewadarId = rowData?.id;
  const email = rowData?.email;
  const code = rowData?.centerCode;
  const loginEmail = localStorage.getItem("email");

  const [permissions, setPermissions] = useState({});
  const [collapsed, setCollapsed] = useState({});
  const [loading, setLoading] = useState(true);

  // Initialize default
  useEffect(() => {
    const init = {};
    allPermissionKeys.forEach((key) => {
      init[key] = { read: false, write: false, edit: false, delete: false };
    });
    setPermissions(init);
  }, []);

  // Fetch from DB
  useEffect(() => {
    if (!sewadarId || !email) return;
    apiClient
      .get(`${SERVER_BASE}/fetchpermissions/${sewadarId}/${email}`)
      .then((res) => {
        const data = typeof res.data === "string" ? JSON.parse(res.data) : res.data || {};
        setPermissions((prev) => ({
          ...prev,
          ...Object.fromEntries(
            allPermissionKeys.map((key) => [
              key,
              {
                read: !!data[key]?.read,
                write: !!data[key]?.write,
                edit: !!data[key]?.edit,
                delete: !!data[key]?.delete,
              },
            ])
          ),
        }));
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [sewadarId, email]);
  
  // Toggle single permission
  const togglePermission = (col, perm) => {
    setPermissions((prev) => ({
      ...prev,
      [col]: { ...prev[col], [perm]: !prev[col][perm] },
    }));
  };

  // Toggle per group-permission (read/write/edit/delete)
  const toggleGroupPermission = (groupKey, perm) => {
    const groupItems = permissionColumnGroups[groupKey].items;
    const allSelected = Object.keys(groupItems).every(
      (col) => permissions[col]?.[perm]
    );
    setPermissions((prev) => {
      const updated = { ...prev };
      Object.keys(groupItems).forEach((col) => {
        updated[col] = { ...prev[col], [perm]: !allSelected };
      });
      return updated;
    });
  };

  // Toggle full access for that group (all read/write/edit/delete)
  const toggleGroupAll = (groupKey) => {
    const groupItems = permissionColumnGroups[groupKey].items;
    const allSelected = Object.keys(groupItems).every((col) =>
      Object.values(permissions[col] || {}).every(Boolean)
    );
    setPermissions((prev) => {
      const updated = { ...prev };
      Object.keys(groupItems).forEach((col) => {
        updated[col] = {
          read: !allSelected,
          write: !allSelected,
          edit: !allSelected,
          delete: !allSelected,
        };
      });
      return updated;
    });
  };

  // Toggle all (Give Full Access)
  const toggleFullAccess = () => {
    const allSelected = Object.values(permissions).every((p) =>
      Object.values(p).every(Boolean)
    );
    const updated = {};
    Object.keys(permissions).forEach((col) => {
      updated[col] = {
        read: !allSelected,
        write: !allSelected,
        edit: !allSelected,
        delete: !allSelected,
      };
    });
    setPermissions(updated);
  };

  const isFullAccessChecked =
    Object.keys(permissions).length > 0 &&
    Object.values(permissions).every((p) =>
      Object.values(p).every(Boolean)
    );

  // Toggle all for one row (Select All column)
  const toggleSelectAllForRow = (col) => {
    const current = permissions[col];
    const allSelected =
      current.read && current.write && current.edit && current.delete;
    setPermissions((prev) => ({
      ...prev,
      [col]: {
        read: !allSelected,
        write: !allSelected,
        edit: !allSelected,
        delete: !allSelected,
      },
    }));
  };

  const handleSave = () => {
    apiClient
      .put(`${SERVER_BASE}/permissions/${sewadarId}`, {
        filePermissions: permissions,
        code,
        email,
        loginEmail
      })
      .then(() => {
        Swal.fire({
          icon: "success",
          text: "Permissions saved successfully!",
          toast: true,
          position: "top-end",
          timer: 1000,
          showConfirmButton: false,
        }).then(() => navigate("../SewadarList"));
      })
      .catch(console.error);
  };

  if (loading)
    return (
      <div className="text-center mt-5">
        <div className="spinner-border text-primary" role="status"></div>
      </div>
    );

  return (
    <div className="formcontainer mt-5">
      <div className="card shadow-lg">
        <div className="card-header bg-success text-white d-flex justify-content-between align-items-center">
          <h5 className="m-0">
            Permissions for {rowData?.firstName} {rowData?.lastName} (
            {rowData?.badgeId})
          </h5>
          <div className="d-flex align-items-center gap-3">
            <div className="form-check">
              <input
                type="checkbox"
                className="form-check-input"
                checked={isFullAccessChecked}
                onChange={toggleFullAccess}
                id='givefull'
              />
              <label className="form-check-label" htmlFor='givefull'>Give Full Access</label>
            </div>
            <button className="btn btn-light btn-sm" onClick={handleSave}>
              Save Permissions
            </button>
          </div>
        </div>

        <div className="card-body table-responsive">
          <table className="table table-bordered table-hover align-middle">
            <thead className="table-primary text-center">
              <tr>
                <th>Section / File Name</th>
                {["read", "write", "edit", "delete"].map((perm) => (
                  <th key={perm}>{perm.toUpperCase()}</th>
                ))}
                <th>Select All</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(permissionColumnGroups).map(([groupKey, group]) => (
                <React.Fragment key={groupKey}>
                  {/* Group Header */}
                  <tr className="table-secondary fw-bold">
                    <td
                      style={{ cursor: "pointer" }}
                      onClick={() =>
                        setCollapsed((prev) => ({
                          ...prev,
                          [groupKey]: !prev[groupKey],
                        }))
                      }
                    >
                      {collapsed[groupKey] ? "▸" : "▾"} {group.label}
                    </td>
                    {["read", "write", "edit", "delete"].map((perm) => (
                      <td key={perm} className="text-center">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={Object.keys(group.items).every(
                            (col) => permissions[col]?.[perm]
                          )}
                          onChange={() => toggleGroupPermission(groupKey, perm)}
                        />
                      </td>
                    ))}
                    <td className="text-center">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={Object.keys(group.items).every((col) =>
                          Object.values(permissions[col] || {}).every(Boolean)
                        )}
                        onChange={() => toggleGroupAll(groupKey)}
                      />
                    </td>
                  </tr>

                  {/* Child rows */}
                  {!collapsed[groupKey] &&
                    Object.entries(group.items).map(([col, label]) => (
                      <tr key={col}>
                        <td>{label}</td>
                        {["read", "write", "edit", "delete"].map((perm) => (
                          <td key={perm} className="text-center">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              checked={permissions[col]?.[perm] || false}
                              onChange={() => togglePermission(col, perm)}
                            />
                          </td>
                        ))}
                        <td className="text-center">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={
                              permissions[col]?.read &&
                              permissions[col]?.write &&
                              permissions[col]?.edit &&
                              permissions[col]?.delete
                            }
                            onChange={() => toggleSelectAllForRow(col)}
                          />
                        </td>
                      </tr>
                    ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PermissionsPage;
